import { useState } from 'react'
import { Activity, ArrowLeft, ChevronDown, ChevronLeft, Minus, Plus, Star, Ticket, Trophy } from 'lucide-react'
import { americanOdds, clockDisplay, competitorNames, lineDisplay, previewFontVariables, previewThemeVariables, resolvePreviewTheme, unsignedHandicap } from './nativePreviewModel'
import { balancedLineIndex, eventMarketGroups, eventMarketPresentation, eventPreviewTabs, isMoneyline, isTotal, marketProbabilities, nativeCategoryTitle, nativeTabTitle, teamLabeledMarket } from './eventMarketPreviewModel'
import { eventTeamColors, formatKickoffTime, formatShortDate, isSGPEligible, pulseTint } from './nativePreviewTeamColors'
import { SECTION_NAMES } from './experienceModel'
import { DeviceChrome, OddsCell, ParlayCard, TeamBadge } from './NativePreview'
import styles from './NativePreview.module.css'
import page from './gamePagePreview.module.css'

const OFFER_ICONS = import.meta.glob('./offerIcons/*.svg', { eager: true, query: '?url', import: 'default' })
const offerIcon = (key) => OFFER_ICONS[`./offerIcons/wager_offer_${key}.svg`]

/**
 * One game page as the apps draw it (EventView.swift, EventScreen.kt): the
 * league title bar, then the page's enabled sections. Sections other than the
 * matchup header and markets show only on the All Markets tab, as in the apps.
 */
export function GamePagePreview({ manifest, event, freshness, presentation, device, appearance = null, selectedSection, onSelect, interactive = true }) {
  const [chosenCategory, setChosenCategory] = useState(null)
  const [groupChoices, setGroupChoices] = useState({})
  const [marketChoices, setMarketChoices] = useState({})
  const theme = resolvePreviewTheme(manifest.theme, appearance)
  const tabs = event ? eventPreviewTabs(event) : []
  const priority = presentation.category_order
  const ordered = tabs.map((tab, index) => ({ tab, index })).sort((a, b) => {
    const left = priority.indexOf(a.tab.key)
    const right = priority.indexOf(b.tab.key)
    return (left < 0 ? Infinity : left) - (right < 0 ? Infinity : right) || a.index - b.index
  }).map((item) => item.tab)
  const category = ordered.some((tab) => tab.key === chosenCategory) ? chosenCategory
    : ordered.some((tab) => tab.key === presentation.initial_category) ? presentation.initial_category : ordered[0]?.key ?? 'all'
  const groups = eventMarketGroups(ordered, category)
  const firstMarketId = groups[0]?.markets[0]?.id
  const allTab = category === 'all' && ordered.length > 0
  const probabilities = event ? marketProbabilities(event, freshness) : []
  const chooseMarket = (id, patch) => setMarketChoices((previous) => ({ ...previous, [id]: { ...previous[id], ...patch } }))
  const sections = presentation.sections.filter((section) => section.enabled).filter((section) => {
    if (section.type === 'event_header' || section.type === 'event_markets') return true
    if (section.type === 'event_probability') return allTab && probabilities.length > 0
    return false
  })
  return (
    <div className={styles.device} style={{ ...previewThemeVariables(theme), ...previewFontVariables(manifest.theme?.typography, device) }} data-device={device} data-appearance={theme.appearance} data-testid="game-page-preview" aria-label="Game page preview">
      <DeviceChrome />
      <NavBar device={device} title={event?.league || 'Game'} />
      <div className={page.scroll} data-scroller="y">
        {!event ? <div className={page.slot}><p className={page.empty}>No games for this page.</p></div> : sections.map((section) => (
          <Slot key={section.id} section={section} selected={selectedSection === section.type} interactive={interactive} onSelect={onSelect}>
            {section.type === 'event_header' ? <MatchupHero event={event} compact={presentation.header_style === 'compact'} /> : null}
            {section.type === 'event_probability' ? <MarketPulse event={event} probabilities={probabilities} /> : null}
            {section.type === 'event_markets' ? <div className={page.markets}>
              <SgpSuggestions event={event} interactive={interactive} />
              {ordered.length ? <div className={page.chips} role="tablist" aria-label="Market categories">
                {ordered.map((tab) => <button type="button" role="tab" key={tab.key} aria-selected={category === tab.key} className={page.chip} onClick={() => setChosenCategory(tab.key)}>{nativeTabTitle(tab, event.sport)}</button>)}
              </div> : <p className={page.empty}>Markets aren’t available for this event right now.</p>}
              {category === 'same_game_parlay' ? <p className={page.sgpHelp}>Choose your legs below. Your betslip checks whether they can be combined and gets a Same Game Parlay price.</p> : null}
              {groups.map((group, index) => {
                const expanded = groupChoices[`${category}:${index}`] ?? true
                return <div key={`${group.key}:${index}`} className={page.group}>
                  <button type="button" className={page.groupHeader} aria-expanded={expanded} onClick={() => setGroupChoices((previous) => ({ ...previous, [`${category}:${index}`]: !expanded }))}>
                    <span className={page.groupIcon} aria-hidden="true" style={{ '--icon': `url("${offerIcon(group.key)}")` }} />
                    <span className={page.groupTitle}>{nativeCategoryTitle(group.key, event.sport)}</span>
                    <span className={page.groupCount}>{group.markets.length}</span>
                    <ChevronDown size={14} strokeWidth={3} className={page.chevron} aria-hidden="true" />
                  </button>
                  {expanded ? group.markets.map((market) => <MarketCard key={market.id} market={teamLabeledMarket(event, market)} event={event} device={device} style={presentation.market_style} featured={market.id === firstMarketId} sameGameParlay={category === 'same_game_parlay'}
                    expanded={marketChoices[market.id]?.expanded ?? (presentation.initially_expanded_markets === 'all' || (presentation.initially_expanded_markets === 'first' && market.id === firstMarketId))}
                    line={marketChoices[market.id]?.line} choose={(patch) => chooseMarket(market.id, patch)} />) : null}
                </div>
              })}
            </div> : null}
          </Slot>
        ))}
      </div>
    </div>
  )
}

// Selecting a section edits it; Enter or Space on a focused section does too.
function Slot({ section, selected, interactive, onSelect, children }) {
  const label = SECTION_NAMES[section.type]
  return (
    <div className={page.slot} data-interactive={interactive ? 'true' : 'false'} data-selected={selected ? 'true' : 'false'} role="group" aria-label={label} tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onSelect?.(section.type) : undefined}
      onKeyDown={interactive ? (keyEvent) => { if (keyEvent.target === keyEvent.currentTarget && (keyEvent.key === 'Enter' || keyEvent.key === ' ')) { keyEvent.preventDefault(); onSelect?.(section.type) } } : undefined}>
      {interactive ? <span className={page.slotLabel} aria-hidden="true">{label}</span> : null}
      <div className={page.slotContent}>{children}</div>
    </div>
  )
}

// EventView.eventToolbar / EventHeader.kt: back to Sportsbook, the league, Betslip.
function NavBar({ device, title }) {
  return (
    <div className={page.navBar} data-device={device}>
      <span className={page.navBack}>{device === 'android' ? <ArrowLeft size={18} aria-hidden="true" /> : <ChevronLeft size={17} strokeWidth={3} aria-hidden="true" />}Sportsbook</span>
      <strong className={page.navTitle}>{title}</strong>
      <span className={page.navBetslip}><Ticket size={device === 'android' ? 16 : 13} aria-hidden="true" />Betslip (0)</span>
    </div>
  )
}

// EventMatchupHero.swift
function MatchupHero({ event, compact }) {
  const names = competitorNames(event)
  const away = event.participants?.find((item) => item.role === 'away' || item.home === false) || event.participants?.[0]
  const home = event.participants?.find((item) => item.role === 'home' || item.home === true) || event.participants?.[1]
  const colors = eventTeamColors(event)
  const awayTint = colors.away || 'var(--np-accent)'
  const homeTint = colors.home || 'var(--np-brand-secondary)'
  const awayName = away?.name || names[0] || 'Away'
  const homeName = home?.name || names[1] || 'Home'
  const soccer = ['SOCCER', 'FOOTBALL'].includes(String(event.sport).toUpperCase())
  return (
    <div className={page.hero} style={{ '--away': awayTint, '--home': homeTint }}>
      <span className={page.heroAtmosphere} aria-hidden="true" />
      <div className={page.heroTop}>
        <span className={page.leagueTag}><Trophy size={10} aria-hidden="true" />{(event.league || '').toUpperCase()}</span>
        <span className={page.heroTopEnd}>
          {event.live ? <span className={page.livePill}><i />LIVE IN-PLAY</span> : null}
          <span className={page.followButton} aria-hidden="true"><Star size={14} strokeWidth={2.5} /></span>
        </span>
      </div>
      {soccer ? <SoccerMatchup event={event} away={away} home={home} awayName={awayName} homeName={homeName} compact={compact} /> : (
        <div className={page.matchup} data-compact={compact ? 'true' : 'false'}>
          <TeamColumn participant={away} name={awayName} tint={awayTint} compact={compact} />
          <div className={page.center}>
            {event.live ? <>
              <span className={page.score}>{event.score ? `${event.score.away_score} - ${event.score.home_score}` : '—'}</span>
              <span className={page.clock}>{event.clock ? clockDisplay(event.clock) : 'LIVE'}</span>
            </> : <>
              <span className={page.vs} aria-hidden="true">VS</span>
              <span className={page.kickoff}>{event.start_time ? formatKickoffTime(event.start_time) : ''}</span>
            </>}
          </div>
          <TeamColumn participant={home} name={homeName} tint={homeTint} compact={compact} />
        </div>
      )}
      <div className={page.heroFooter}>
        <span className={page.heroDate}>{event.start_time ? formatShortDate(event.start_time) : ''}</span>
        <span className={page.heroPills}>
          {isSGPEligible(event) ? <span className={page.sgpPill}><Ticket size={10} aria-hidden="true" />SGP</span> : null}
          <span className={page.countPill}>{event.markets?.length || 0} MARKETS</span>
        </span>
      </div>
    </div>
  )
}

function TeamColumn({ participant, name, tint, compact }) {
  const size = compact ? 44 : 72
  return (
    <div className={page.team}>
      <span className={page.teamBadge} style={{ '--tint': tint, '--size': `${size}px` }}>
        <TeamBadge participant={participant} size={compact ? 28 : 46} />
        {!participant?.presentation?.primary_color ? <span className={page.teamInitial}>{name.charAt(0)}</span> : null}
      </span>
      <strong className={page.teamName}>{name}</strong>
    </div>
  )
}

// Soccer lists the home side first, inline when both names are short.
function SoccerMatchup({ event, away, home, awayName, homeName, compact }) {
  const logo = compact ? 20 : 24
  const status = event.live ? (event.clock ? clockDisplay(event.clock) : 'LIVE') : `${formatShortDate(event.start_time)}, ${formatKickoffTime(event.start_time)}`
  if (homeName.length <= 16 && awayName.length <= 16) {
    return (
      <div className={page.soccerInline}>
        <div className={page.soccerTeams}>
          <span className={page.soccerHome}><TeamBadge participant={home} size={logo} />{homeName}</span>
          <span className={page.soccerVersus} aria-hidden="true">v</span>
          <span className={page.soccerAway}>{awayName}<TeamBadge participant={away} size={logo} /></span>
        </div>
        {event.live ? <span className={page.soccerLive}><b>{event.score ? `${event.score.home_score} - ${event.score.away_score}` : '—'}</b><i>·</i><em>{status}</em></span> : null}
      </div>
    )
  }
  return (
    <div className={page.soccerStacked}>
      {[[home, homeName, event.score?.home_score], [away, awayName, event.score?.away_score]].map(([participant, name, score]) => (
        <div key={name} className={page.soccerRow}><TeamBadge participant={participant} size={logo} /><span>{name}</span>{event.live ? <b>{score ?? '0'}</b> : null}</div>
      ))}
      <span className={page.soccerStatus} data-live={event.live ? 'true' : 'false'}>{event.live ? <i /> : null}{status}</span>
    </div>
  )
}

// MarketProbabilitySection (EventInsights.swift)
function MarketPulse({ event, probabilities }) {
  const market = event.markets.find((item) => item.outcomes.some((outcome) => outcome.id === probabilities[0].id))
  return (
    <div className={page.pulse}>
      <div className={page.pulseHeader}>
        <span className={page.pulseTitle}><Activity size={12} aria-hidden="true" />MARKET PULSE</span>
        <span className={page.pulseBadge}>ODDS-DERIVED SHARE</span>
      </div>
      <span className={page.pulseMarket}>{(market?.label || 'Moneyline').toUpperCase()}</span>
      <div className={page.pulseBar} aria-hidden="true">
        {probabilities.map((item, index) => <span key={item.id} style={{ flexGrow: item.share, '--tint': pulseTint(item, event, index, probabilities.length) }} />)}
      </div>
      <div className={page.pulseColumns}>
        {probabilities.map((item, index) => {
          const outcome = market ? teamLabeledMarket(event, market).outcomes.find((candidate) => candidate.id === item.id) : null
          return <div key={item.id} className={page.pulseColumn}>
            <div className={page.pulseRow}>
              <span className={page.pulseLabel}><i style={{ background: pulseTint(item, event, index, probabilities.length) }} />{item.label}</span>
              <b className={page.pulseShare}>{(item.share * 100).toFixed(1)}%</b>
            </div>
            {outcome ? <span className={page.tallOdds}><OddsCell outcome={outcome} marketType="MONEYLINE" marketStatus={market.status} /></span> : null}
          </div>
        })}
      </div>
      <span className={page.pulseNote}>Prices describe posted odds, not an independent prediction.</span>
    </div>
  )
}

// RecommendedSGPBuilder: the favorite to win with the over, then with the under.
function SgpSuggestions({ event, interactive }) {
  const moneyline = event.markets.find((market) => market.status === 'OPEN' && isMoneyline(market))
  const total = event.markets.find((market) => market.status === 'OPEN' && isTotal(market))
  const priced = (outcome) => outcome.status === 'OPEN' && outcome.odds != null
  const favorite = moneyline?.outcomes.filter(priced).sort((a, b) => a.odds - b.odds)[0]
  const over = total?.outcomes.find((outcome) => priced(outcome) && outcome.label.toLowerCase().startsWith('over'))
  const under = total?.outcomes.find((outcome) => priced(outcome) && outcome.label.toLowerCase().startsWith('under'))
  if (!favorite || !over || !under) return null
  const catalog = { events: [event] }
  const card = (id, side, outcome) => ({ id, kind: 'parlay_suggestion', headline: `${favorite.label} to Win & ${side}`, legs: [
    { event_id: event.id, market_id: moneyline.id, outcome_id: favorite.id },
    { event_id: event.id, market_id: total.id, outcome_id: outcome.id },
  ] })
  return (
    <div className={page.sgp}>
      <div className={page.sgpHeader}>
        <span className={page.sgpIcon} aria-hidden="true"><Ticket size={14} /></span>
        <span><strong>Same Game Parlays</strong><small>Picked from this game’s markets</small></span>
      </div>
      <div className={page.sgpShelf}>
        {[card('over', 'Over', over), card('under', 'Under', under)].map((item) => <div key={item.id} className={page.sgpItem}><ParlayCard card={item} catalog={catalog} interactive={interactive} showEvent={false} /></div>)}
      </div>
    </div>
  )
}

// MarketAccordionCard, SpreadLineSlider and OutcomeCarouselCard.
function MarketCard({ market, event, device, style, featured, sameGameParlay, expanded, line, choose }) {
  const { kind, pairs } = eventMarketPresentation(market, event, style, device)
  const index = line === undefined ? balancedLineIndex(pairs) : pairs.reduce((nearest, pair, position) => Math.abs(pair.line - line) < Math.abs(pairs[nearest].line - line) ? position : nearest, 0)
  const pair = pairs[Math.min(index, pairs.length - 1)]
  const toggle = () => choose({ expanded: !expanded })
  return (
    <div className={page.marketCard} data-kind={kind} data-sgp={sameGameParlay ? 'true' : 'false'} role="group" aria-label={market.label}>
      <button type="button" className={page.marketHeader} aria-expanded={expanded} aria-label={market.label} onClick={toggle}>
        <span className={page.marketTitle}>{market.label.toUpperCase()}</span>
        {kind === 'slider' && pair ? <span className={page.lineBadge}>{pair.total ? unsignedHandicap(pair.line) : lineDisplay(pair.line)}</span> : null}
        {kind === 'carousel' ? <span className={page.optionCount}>{market.outcomes.length} OPTIONS</span> : null}
        {kind === 'grid' && featured ? <span className={page.featured}>FEATURED</span> : null}
        <ChevronDown size={13} strokeWidth={3} className={page.chevron} aria-hidden="true" />
      </button>
      {expanded && kind === 'slider' ? <>
        <div className={page.sliderRow}>
          <button type="button" className={page.stepButton} aria-label="Previous available line" disabled={index <= 0} onClick={() => choose({ line: pairs[index - 1].line })}><Minus size={12} strokeWidth={3} /></button>
          <input type="range" className={page.slider} aria-label={`${market.label} line`} min="0" max={pairs.length - 1} step="1" value={index} onChange={(change) => choose({ line: pairs[Number(change.target.value)].line })} />
          <button type="button" className={page.stepButton} aria-label="Next available line" disabled={index >= pairs.length - 1} onClick={() => choose({ line: pairs[index + 1].line })}><Plus size={12} strokeWidth={3} /></button>
        </div>
        <div className={page.outcomes}>{pair.outcomes.map((outcome) => <span key={outcome.id} className={page.tallOdds}><OddsCell outcome={outcome} marketType={pair.total ? 'TOTAL' : 'SPREAD'} marketStatus={market.status} /></span>)}</div>
      </> : null}
      {expanded && kind === 'carousel' ? <div className={page.carousel}>
        {market.outcomes.map((outcome) => <span key={outcome.id} className={page.carouselCard} data-open={market.status === 'OPEN' && outcome.status === 'OPEN' ? 'true' : 'false'}>
          <span>{outcome.label}</span><b>{outcome.odds_american || americanOdds(outcome.odds)}</b>
        </span>)}
      </div> : null}
      {expanded && kind === 'grid' ? <div className={page.outcomes}>{market.outcomes.map((outcome) => <span key={outcome.id} className={page.tallOdds}><OddsCell outcome={outcome} marketType={market.type} marketStatus={market.status} /></span>)}</div> : null}
    </div>
  )
}
